import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, X, Cpu } from 'react-feather';
import { useTranslations } from 'use-intl';
import { SocialNetworkProfileRecord } from '@/pages/CustomerSupport/types';

interface AIInterviewAnalysis {
	reasoning?: string;
	reasoning_es?: string;
	financial_literacy?: number;
	payment_confidence?: number;
	lifestyle_coherence?: number;
	personality_consistency?: number;
	score_interpretation_es?: string;
}

interface SocialNetworkAnalysisSectionProps {
	socialNetworkProfiles: SocialNetworkProfileRecord[];
	isExpanded: boolean;
	aiInterviewAnalysis?: AIInterviewAnalysis | null;
}

const SocialNetworkAnalysisSection: React.FC<SocialNetworkAnalysisSectionProps> = ({
	socialNetworkProfiles,
	isExpanded: parentIsExpanded,
	aiInterviewAnalysis
}) => {
	const t = useTranslations('SupportClients');
	const [isExpanded, setIsExpanded] = useState(parentIsExpanded);
	const [isAIModalOpen, setIsAIModalOpen] = useState(false);

	useEffect(() => {
		setIsExpanded(parentIsExpanded);
	}, [parentIsExpanded]);

	const getScoreColor = (score: number | null) => {
		if (score === null) return '#6B7280';
		if (score <= 40) return '#EF4444';
		if (score <= 70) return '#F59E0B';
		return '#10B981';
	};

	const getScoreBackgroundColor = (score: number | null) => {
		if (score === null) return '#F3F4F6';
		if (score <= 40) return '#FEE2E2';
		if (score <= 70) return '#FEF3C7';
		return '#D1FAE5';
	};

	const getPlatformLabel = (platform: string | null) => {
		if (!platform) return 'N/A';
		const labels: Record<string, string> = {
			instagram: 'Instagram',
			facebook: 'Facebook',
			tiktok: 'TikTok',
			linkedin: 'LinkedIn'
		};
		return labels[platform] || platform;
	};

	const getPlatformColor = (platform: string | null) => {
		if (!platform) return '#6B7280';
		const colors: Record<string, string> = {
			instagram: '#E1306C',
			facebook: '#1877F2',
			tiktok: '#000000',
			linkedin: '#0A66C2'
		};
		return colors[platform] || '#6B7280';
	};

	const getPlatformBackgroundColor = (platform: string | null) => {
		if (!platform) return '#F3F4F6';
		const backgrounds: Record<string, string> = {
			instagram: '#fccee8',
			facebook: '#EBF5FF',
			tiktok: '#F3F4F6',
			linkedin: '#E7F3FF'
		};
		return backgrounds[platform] || '#F3F4F6';
	};

	const getProfileMatchReasoning = (
		reasoning: string | null,
		reasoningSpanish: string | null | undefined
	) => {
		if (reasoning === null && reasoningSpanish === null) return 'N/A';
		return reasoningSpanish || reasoning;
	};

	const getAIScoreColor = (score: number) => {
		if (score >= 0.8) return 'text-green-600';
		if (score >= 0.6) return 'text-yellow-600';
		return 'text-red-600';
	};

	const getAIScoreBgColor = (score: number) => {
		if (score >= 0.8) return 'bg-green-50';
		if (score >= 0.6) return 'bg-yellow-50';
		return 'bg-red-50';
	};

	const renderAIInterviewModal = () => {
		if (!isAIModalOpen || !aiInterviewAnalysis) return null;

		return (
			<>
				<div className="fixed inset-0 bg-black/50 z-50" onClick={() => setIsAIModalOpen(false)} />
				<div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-50 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
					<div className="flex items-center justify-between p-4 border-b border-gray-200">
						<h2 className="text-lg font-semibold">{t('AIInterviewAnalysis')}</h2>
						<button
							onClick={() => setIsAIModalOpen(false)}
							className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
						>
							<X size={20} />
						</button>
					</div>
					<div className="p-6 space-y-4">
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							{aiInterviewAnalysis.financial_literacy !== undefined && (
								<div
									className={`p-3 rounded-lg ${getAIScoreBgColor(aiInterviewAnalysis.financial_literacy)}`}
								>
									<div className="text-xs text-gray-600 mb-1">{t('FinancialLiteracy')}</div>
									<div
										className={`text-2xl font-bold ${getAIScoreColor(aiInterviewAnalysis.financial_literacy)}`}
									>
										{(aiInterviewAnalysis.financial_literacy * 100).toFixed(0)}%
									</div>
								</div>
							)}
							{aiInterviewAnalysis.payment_confidence !== undefined && (
								<div
									className={`p-3 rounded-lg ${getAIScoreBgColor(aiInterviewAnalysis.payment_confidence)}`}
								>
									<div className="text-xs text-gray-600 mb-1">{t('PaymentConfidence')}</div>
									<div
										className={`text-2xl font-bold ${getAIScoreColor(aiInterviewAnalysis.payment_confidence)}`}
									>
										{(aiInterviewAnalysis.payment_confidence * 100).toFixed(0)}%
									</div>
								</div>
							)}
							{aiInterviewAnalysis.lifestyle_coherence !== undefined && (
								<div
									className={`p-3 rounded-lg ${getAIScoreBgColor(aiInterviewAnalysis.lifestyle_coherence)}`}
								>
									<div className="text-xs text-gray-600 mb-1">{t('LifestyleCoherence')}</div>
									<div
										className={`text-2xl font-bold ${getAIScoreColor(aiInterviewAnalysis.lifestyle_coherence)}`}
									>
										{(aiInterviewAnalysis.lifestyle_coherence * 100).toFixed(0)}%
									</div>
								</div>
							)}
							{aiInterviewAnalysis.personality_consistency !== undefined && (
								<div
									className={`p-3 rounded-lg ${getAIScoreBgColor(aiInterviewAnalysis.personality_consistency)}`}
								>
									<div className="text-xs text-gray-600 mb-1">{t('PersonalityConsistency')}</div>
									<div
										className={`text-2xl font-bold ${getAIScoreColor(aiInterviewAnalysis.personality_consistency)}`}
									>
										{(aiInterviewAnalysis.personality_consistency * 100).toFixed(0)}%
									</div>
								</div>
							)}
						</div>
						{aiInterviewAnalysis.score_interpretation_es && (
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<h3 className="text-sm font-semibold text-blue-900 mb-2">{t('Interpretation')}</h3>
								<p className="text-sm text-blue-800 leading-relaxed">
									{aiInterviewAnalysis.score_interpretation_es}
								</p>
							</div>
						)}
						{aiInterviewAnalysis.reasoning_es && (
							<div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
								<h3 className="text-sm font-semibold text-gray-900 mb-2">
									{t('DetailedReasoning')}
								</h3>
								<p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
									{aiInterviewAnalysis.reasoning_es}
								</p>
							</div>
						)}
					</div>
				</div>
			</>
		);
	};

	if (socialNetworkProfiles.length === 0) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
				<div
					className="flex items-center justify-between cursor-pointer mb-4"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<h2 className="text-lg font-semibold">{t('SocialNetworkAnalysis')}</h2>
					{isExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
				{isExpanded && <p className="text-gray-500 text-center py-6">{t('NoSocialNetworkData')}</p>}
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
			<div
				className="flex items-center justify-between cursor-pointer mb-4"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<div className="flex items-center gap-3">
					<h2 className="text-lg font-semibold">
						{t('SocialNetworkAnalysis')} ({socialNetworkProfiles.length})
					</h2>
					{aiInterviewAnalysis && (
						<button
							onClick={(e) => {
								e.stopPropagation();
								setIsAIModalOpen(true);
							}}
							className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium hover:bg-purple-200 transition-colors"
							title={t('AIInterviewAnalysis')}
						>
							<Cpu size={12} />
							<span>AI</span>
						</button>
					)}
				</div>
				{isExpanded ? (
					<ChevronUp size={20} className="text-gray-500" />
				) : (
					<ChevronDown size={20} className="text-gray-500" />
				)}
			</div>
			{renderAIInterviewModal()}
			{isExpanded && (
				<div className="flex flex-col gap-6">
					{socialNetworkProfiles.map((profile, index) => (
						<div key={profile.id} className="border border-gray-200 rounded-lg p-4">
							{index > 0 && <div className="border-t border-gray-200 -mx-4 mb-4"></div>}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="md:col-span-2">
									<label className="block text-sm text-gray-500 mb-1">
										{t('SocialNetworkUrl')}
									</label>
									{profile.social_network_url ? (
										<a
											href={profile.social_network_url}
											target="_blank"
											rel="noopener noreferrer"
											className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1"
										>
											{profile.social_network_url}
											<ExternalLink size={14} />
										</a>
									) : (
										<p className="text-sm font-medium">N/A</p>
									)}
								</div>

								<div>
									<label className="block text-sm text-gray-500 mb-1">{t('Platform')}</label>
									<span
										className="inline-block px-3 py-1 rounded-xl text-xs font-medium"
										style={{
											backgroundColor: getPlatformBackgroundColor(profile.platform),
											color: getPlatformColor(profile.platform)
										}}
									>
										{getPlatformLabel(profile.platform)}
									</span>
								</div>

								<div>
									<label className="block text-sm text-gray-500 mb-1">
										{t('ProfileMatchScore')}
									</label>
									<span
										className="inline-block px-3 py-1 rounded-xl text-lg font-medium"
										style={{
											backgroundColor: getScoreBackgroundColor(profile.profile_match_score),
											color: getScoreColor(profile.profile_match_score)
										}}
									>
										{profile.profile_match_score !== null
											? `${profile.profile_match_score}%`
											: 'N/A'}
									</span>
								</div>

								{profile.profile_match_reasoning && (
									<div className="md:col-span-2">
										<label className="block text-sm text-gray-500 mb-1">
											{t('ProfileMatchReasoning')}
										</label>
										<p className="text-sm font-medium">
											{getProfileMatchReasoning(
												profile.profile_match_reasoning,
												profile.profile_match_reasoning_spanish || null
											)}
										</p>
									</div>
								)}

								{profile.activity_12_months !== null && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">
											{t('Activity12Months')}
										</label>
										<p className="text-sm font-medium">{profile.activity_12_months * 100}%</p>
									</div>
								)}

								{profile.followers_following_ratio !== null && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">
											{t('FollowersFollowingRatio')}
										</label>
										<p className="text-sm font-medium">
											{profile.followers_following_ratio * 100}%
										</p>
									</div>
								)}

								{profile.profile_consistency !== null && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">
											{t('ProfileConsistency')}
										</label>
										<p className="text-sm font-medium">{profile.profile_consistency * 100}%</p>
									</div>
								)}

								{profile.payment_confidence !== null && (
									<div>
										<label className="block text-sm text-gray-500 mb-1">
											{t('PaymentConfidence')}
										</label>
										<p className="text-sm font-medium">{profile.payment_confidence * 100}%</p>
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default SocialNetworkAnalysisSection;
